package com.qrkontaq.utils

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import kotlinx.browser.document

object SeoHelper {
    private fun setMetaTag(name: String, content: String, isProperty: Boolean = false) {
        val selector = if (isProperty) "meta[property='$name']" else "meta[name='$name']"
        // The kobweb export can bake the runtime-created tag alongside the static
        // one from build.gradle.kts; crawlers then see two conflicting values.
        // Update the first and drop the extras.
        val matches = document.querySelectorAll(selector)
        for (i in matches.length - 1 downTo 1) {
            val node = matches.item(i) ?: continue
            node.parentNode?.removeChild(node)
        }
        val element = document.querySelector(selector)
        if (element != null) {
            element.setAttribute("content", content)
        } else {
            val meta = document.createElement("meta")
            if (isProperty) {
                meta.setAttribute("property", name)
            } else {
                meta.setAttribute("name", name)
            }
            meta.setAttribute("content", content)
            document.head?.appendChild(meta)
        }
    }

    /**
     * Drops duplicate head tags the kobweb export bakes in twice (its own
     * defaults plus the static head from build.gradle.kts) — one viewport and
     * one description must survive, first occurrence wins.
     */
    fun dedupeHeadTags() {
        listOf("meta[name='viewport']", "meta[name='description']").forEach { selector ->
            val matches = document.querySelectorAll(selector)
            for (i in matches.length - 1 downTo 1) {
                val node = matches.item(i) ?: continue
                node.parentNode?.removeChild(node)
            }
        }
    }

    /** Sets (or creates) the robots meta — e.g. "noindex, follow" on utility pages like /auth. */
    fun setRobots(content: String) = setMetaTag("robots", content)

    private fun jsonEscape(s: String): String =
        s.replace("\\", "\\\\").replace("\"", "\\\"").replace("\n", "\\n")

    /**
     * Injects (or replaces) the FAQPage JSON-LD for the landing. Lives here at
     * runtime — not in the baked head — so the export writes it into index.html
     * only, in the language the page actually renders.
     */
    fun setFaqJsonLd(faqs: List<Pair<String, String>>) {
        val head = document.head ?: return
        val script = document.querySelector("script#qk-faq-jsonld") ?: document.createElement("script").also {
            it.setAttribute("id", "qk-faq-jsonld")
            it.setAttribute("type", "application/ld+json")
            head.appendChild(it)
        }
        val entities = faqs.joinToString(",") { (q, a) ->
            """{"@type":"Question","name":"${jsonEscape(q)}",""" +
                """"acceptedAnswer":{"@type":"Answer","text":"${jsonEscape(a)}"}}"""
        }
        script.textContent =
            """{"@context":"https://schema.org","@type":"FAQPage","mainEntity":[$entities]}"""
    }

    /** Points (or creates) the canonical link — search engines pick it up from the rendered DOM. */
    fun setCanonical(href: String) {
        val head = document.head ?: return
        val link = document.querySelector("link[rel='canonical']") ?: document.createElement("link").also {
            it.setAttribute("rel", "canonical")
            head.appendChild(it)
        }
        link.setAttribute("href", href)
    }

    /**
     * Swaps the favicon (and apple-touch-icon) to [href]. Existing icon links
     * are dropped first so a tenant logo wins over the baked-in QRkontaq mark.
     */
    fun setFavicon(href: String) {
        val head = document.head ?: return
        val existing = document.querySelectorAll("link[rel~='icon'], link[rel='apple-touch-icon']")
        for (i in existing.length - 1 downTo 0) {
            val node = existing.item(i) ?: continue
            node.parentNode?.removeChild(node)
        }
        listOf("icon", "apple-touch-icon").forEach { rel ->
            val link = document.createElement("link")
            link.setAttribute("rel", rel)
            link.setAttribute("href", href)
            head.appendChild(link)
        }
    }

    /**
     * Rebrands the tab + link previews for a venue subdomain: the restaurant's
     * name becomes the title, its cover becomes the OG/Twitter image (the banner
     * shown when the menu link is shared), and its logo becomes the favicon.
     * Missing fields keep the QRkontaq defaults.
     */
    fun applyTenantBranding(
        name: String,
        description: String = "",
        logoUrl: String? = null,
        coverUrl: String? = null,
    ) {
        document.title = name
        setMetaTag("og:title", name, isProperty = true)
        setMetaTag("twitter:title", name)
        if (description.isNotBlank()) {
            setMetaTag("description", description)
            setMetaTag("og:description", description, isProperty = true)
            setMetaTag("twitter:description", description)
        }
        if (!coverUrl.isNullOrBlank()) {
            setMetaTag("og:image", coverUrl, isProperty = true)
            setMetaTag("twitter:image", coverUrl)
        }
        if (!logoUrl.isNullOrBlank()) {
            setFavicon(logoUrl)
        }
        // The baked head targets the platform landing: its hreflang alternates
        // and canonical point at qrkontaq.com and would tell crawlers this venue
        // site is just a duplicate of the landing. Rebind them to the tenant.
        val alternates = document.querySelectorAll("link[rel='alternate'][hreflang]")
        for (i in alternates.length - 1 downTo 0) {
            val node = alternates.item(i) ?: continue
            node.parentNode?.removeChild(node)
        }
        val origin = kotlinx.browser.window.location.origin
        setCanonical("$origin/")
        setMetaTag("og:url", "$origin/", isProperty = true)
    }

    fun updatePageMeta(
        title: String,
        description: String = "",
        // Absolute URL: link-preview scrapers don't resolve relative og:image paths.
        ogImage: String = "https://qrkontaq.com/og-image.png",
        ogType: String = "website",
        // When set, og:url follows the page instead of the baked-in homepage —
        // otherwise /compare shared on social unfurls as the landing.
        ogUrl: String? = null,
    ) {
        document.title = title

        if (description.isNotBlank()) {
            setMetaTag("description", description)
        }
        ogUrl?.let { setMetaTag("og:url", it, isProperty = true) }

        setMetaTag("og:title", title, isProperty = true)
        setMetaTag("og:type", ogType, isProperty = true)
        if (description.isNotBlank()) {
            setMetaTag("og:description", description, isProperty = true)
        }
        setMetaTag("og:image", ogImage, isProperty = true)

        setMetaTag("twitter:title", title)
        if (description.isNotBlank()) {
            setMetaTag("twitter:description", description)
        }
    }
}

@Composable
fun PageSeo(
    title: String,
    description: String = "",
    ogImage: String = "https://qrkontaq.com/og-image.png"
) {
    LaunchedEffect(title, description) {
        SeoHelper.updatePageMeta(
            title = title,
            description = description,
            ogImage = ogImage
        )
    }
}
